import { Endpoints } from '@/lib/Endpoints';
import { getApiBaseUrl } from '@/lib/apiBaseUrl';
import { getIsAnonymous, getLocalUser } from '@/lib/LocalStorage';

export type CheckoutFlow = 'checkout-a' | 'checkout-b';

export type CheckoutBStep =
  | 'basket'
  | 'receiver'
  | 'extras'
  | 'photo'
  | 'card'
  | 'bill'
  | 'payment';

export type CheckoutBErrorKind = 'api' | 'validation' | 'unexpected';

const REPORT_DEDUPE_TTL_MS = 10_000;
const recentReports = new Map<string, number>();

function truncate(value: string | undefined, max: number): string | undefined {
  if (!value) return undefined;
  return value.length <= max ? value : `${value.slice(0, max)}…(truncated)`;
}

function requestPath(url: string): string {
  const bare = url.split('?')[0] || '';
  try {
    return new URL(bare, 'https://local.invalid').pathname || bare;
  } catch {
    return bare;
  }
}

function axiosContext(error: unknown) {
  const anyErr = error as {
    isAxiosError?: boolean;
    name?: string;
    config?: { url?: string; method?: string };
    response?: { status?: number };
  } | null;
  if (!anyErr || (anyErr.isAxiosError !== true && anyErr.name !== 'AxiosError')) {
    return null;
  }
  const axiosUrl = requestPath(String(anyErr.config?.url || ''));
  return {
    axiosUrl,
    axiosMethod: String(anyErr.config?.method || '').toUpperCase(),
    axiosStatus: Number(anyErr.response?.status || 0) || 0,
  };
}

function pageLocation(): { href: string; pageHost?: string } {
  const host = window.location?.hostname || '';
  const path = `${window.location?.pathname || ''}${window.location?.search || ''}`;
  if (host === 'localhost' || host === '127.0.0.1') {
    return { href: path || '/', pageHost: host };
  }
  return { href: window.location?.href || '' };
}

function loggedInUserId(): number | undefined {
  if (getIsAnonymous()) return undefined;
  const user = getLocalUser();
  if (!user || Number(user.isAnonymous) === 1) return undefined;
  const id = Number(user.id);
  if (!Number.isInteger(id) || id <= 0) return undefined;
  return id;
}

function postClientError(payload: Record<string, unknown>) {
  const body = JSON.stringify(payload);
  const url = `${getApiBaseUrl()}${Endpoints.CLIENT_ERROR_REPORT}`;

  let crossOrigin = true;
  try {
    crossOrigin = new URL(url, window.location.href).origin !== window.location.origin;
  } catch {
    crossOrigin = true;
  }

  try {
    if (
      !crossOrigin &&
      typeof navigator !== 'undefined' &&
      typeof navigator.sendBeacon === 'function'
    ) {
      const blob = new Blob([body], { type: 'application/json' });
      if (navigator.sendBeacon(url, blob)) return;
    }
  } catch {
    // fetch below
  }

  void fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body,
    keepalive: true,
    credentials: 'omit',
  }).catch(() => {});
}

export function reportCheckoutAError(opts: {
  step: CheckoutBStep;
  action: string;
  kind: CheckoutBErrorKind;
  message: string;
  error?: unknown;
}) {
  reportCheckoutBError({ ...opts, flow: 'checkout-a' });
}

export function reportCheckoutBError(opts: {
  flow?: CheckoutFlow;
  step: CheckoutBStep;
  action: string;
  kind: CheckoutBErrorKind;
  message: string;
  error?: unknown;
}) {
  if (typeof window === 'undefined') return;

  const flow: CheckoutFlow = opts.flow === 'checkout-a' ? 'checkout-a' : 'checkout-b';
  const step = opts.step;
  const action = String(opts.action || 'unknown').slice(0, 64);
  const kind = opts.kind;
  const shown = truncate(String(opts.message || 'Bir hata oluştu'), 500) || 'Bir hata oluştu';
  const summary = `[${flow}][${step}][${action}] ${shown}`;

  const dedupeKey = `${flow}::${step}::${action}::${shown}`;
  const now = Date.now();
  const last = recentReports.get(dedupeKey) || 0;
  if (now - last < REPORT_DEDUPE_TTL_MS) return;
  recentReports.set(dedupeKey, now);

  const err = opts.error instanceof Error ? opts.error : null;
  const axios = axiosContext(opts.error);
  const userId = loggedInUserId();
  const page = pageLocation();

  postClientError({
    message: summary,
    name: err?.name || 'CheckoutError',
    ...(userId ? { userId } : {}),
    stack: truncate(err?.stack, 2000),
    href: page.href,
    ...(page.pageHost ? { pageHost: page.pageHost } : {}),
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent || '' : '',
    ts: new Date().toISOString(),
    flow,
    step,
    action,
    kind,
    ...(axios && {
      axiosUrl: truncate(axios.axiosUrl, 500),
      axiosMethod: axios.axiosMethod,
      axiosStatus: axios.axiosStatus,
    }),
  });
}
